import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Card from '../ui/Card';

const RequestLoanForm = ({ chamaId, onSuccess, onCancel, initialValues }) => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState(() => ({
    amount: initialValues?.amount ?? '',
    purpose: initialValues?.purpose ?? '',
    repayment_period_months: initialValues?.repayment_period_months ?? '6',
    interest_rate: initialValues?.interest_rate ?? '5',
  }));

  useEffect(() => {
    if (!initialValues) return;
    setFormData((prev) => ({
      ...prev,
      amount: initialValues.amount ?? prev.amount,
      purpose: initialValues.purpose ?? prev.purpose,
      repayment_period_months:
        initialValues.repayment_period_months ?? prev.repayment_period_months,
      interest_rate: initialValues.interest_rate ?? prev.interest_rate,
    }));
  }, [initialValues]);

  // Calculate loan details
  const loanAmount = parseFloat(formData.amount) || 0;
  const interestRate = parseFloat(formData.interest_rate) || 0;
  const months = parseInt(formData.repayment_period_months, 10) || 1;
  const interestAmount = (loanAmount * interestRate * months) / 100;
  const totalRepayment = loanAmount + interestAmount;
  const monthlyPayment = totalRepayment / months;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setErrors({});

      await api.post('/loans', {
        ...formData,
        chama_id: chamaId,
      });

      onSuccess?.();
    } catch (error) {
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setErrors({ general: 'Failed to request loan' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {errors.general && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-sm text-red-800">
          {errors.general}
        </div>
      )}

      <Input
        label="Loan Amount (KES)"
        name="amount"
        type="number"
        value={formData.amount}
        onChange={handleChange}
        error={errors.amount?.[0]}
        placeholder="e.g., 50000"
        required
      />

      <div>
        <label htmlFor="loan-purpose" className="block text-sm font-medium text-gray-700 mb-2">
          Purpose
        </label>
        <textarea
          id="loan-purpose"
          name="purpose"
          value={formData.purpose}
          onChange={handleChange}
          rows={3}
          className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 transition-all resize-none"
          placeholder="What will you use the loan for?"
          required
        />
        {errors.purpose && <p className="mt-2 text-sm text-red-600">{errors.purpose[0]}</p>}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <Input
          label="Repayment Period (Months)"
          name="repayment_period_months"
          type="number"
          min="1"
          max="24"
          value={formData.repayment_period_months}
          onChange={handleChange}
          error={errors.repayment_period_months?.[0]}
          required
        />

        <Input
          label="Interest Rate (%)"
          name="interest_rate"
          type="number"
          step="0.1"
          value={formData.interest_rate}
          onChange={handleChange}
          error={errors.interest_rate?.[0]}
          helpText="Per month"
          required
        />
      </div>

      {/* Loan Summary */}
      {loanAmount > 0 && (
        <Card className="bg-sky-50 border-sky-200">
          <h4 className="font-semibold text-gray-900 mb-3">Loan Summary</h4>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Principal Amount:</span>
              <span className="font-semibold">KES {loanAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">
                Interest ({interestRate}% × {months} months):
              </span>
              <span className="font-semibold">KES {interestAmount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between pt-2 border-t border-sky-300">
              <span className="text-gray-900 font-semibold">Total Repayment:</span>
              <span className="font-bold text-lg text-emerald-600">
                KES {totalRepayment.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Monthly Payment:</span>
              <span className="font-semibold">KES {monthlyPayment.toLocaleString()}</span>
            </div>
          </div>
        </Card>
      )}

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex gap-3">
          <svg
            className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="text-sm text-amber-800">
            <p className="font-semibold mb-1">Important:</p>
            <ul className="space-y-1 text-xs">
              <li>• Your loan request will be reviewed by chama admins</li>
              <li>• Approval may take 24-48 hours</li>
              <li>• Ensure you can meet the monthly payment obligations</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="flex gap-4 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} fullWidth disabled={loading}>
          Cancel
        </Button>
        <Button type="submit" fullWidth loading={loading}>
          Submit Request
        </Button>
      </div>
    </form>
  );
};

export default RequestLoanForm;
